import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { attributionViewerKeys } from './queryKeys'

import { getMeetingAttributionShipped } from '@/api/internal'

const SUMMARY_POLL_MS = 10_000

export function useShippedRecord({
  meetingId,
  revision,
  enabled,
}: {
  meetingId: string
  revision: string | null
  enabled: boolean
}) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: attributionViewerKeys.shippedRecord(meetingId, revision),
    enabled,
    // Without a revision the server still answers for the current one, which is how not_shipped and missing show.
    queryFn: async ({ signal }) => (await getMeetingAttributionShipped(meetingId, revision, signal)).data,
    // LLM names land when the summary finishes, and a mismatch clears once the transcript reload catches up.
    refetchInterval: (current) => {
      const status = current.state.data?.status
      return status === 'summary_pending' || status === 'revision_mismatch' ? SUMMARY_POLL_MS : false
    },
    staleTime: Infinity,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const mismatchedAt = query.data?.status === 'revision_mismatch' ? query.dataUpdatedAt : null
  // A newer refine replaced the displayed transcript; reload it so the record and the lanes describe one revision.
  useEffect(() => {
    if (mismatchedAt !== null)
      void queryClient.invalidateQueries({ queryKey: attributionViewerKeys.artifactScope(meetingId) })
  }, [mismatchedAt, meetingId, queryClient])
  return query
}
